import React, { useState, useEffect } from 'react';
import { Button, Input, Label } from '@lnr-v2/ui';
import type { CreateClientDto, UpdateClientDto, Client } from '@lnr-v2/types';
import { ImageUploader } from '../molecules/ImageUploader';

interface ClientFormSimpleProps {
  initialData?: Client;
  onSubmit: (data: CreateClientDto | UpdateClientDto) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const ClientFormSimple: React.FC<ClientFormSimpleProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<CreateClientDto>({
    name: initialData?.name || '',
    logo: initialData?.logo || '',
    description: initialData?.description || '',
    website: initialData?.website || '',
    industry: initialData?.industry || '',
    contactName: initialData?.contactName || '',
    contactEmail: initialData?.contactEmail || '',
    contactPhone: initialData?.contactPhone || '',
    address: initialData?.address || '',
    city: initialData?.city || '',
    country: initialData?.country || '',
    isActive: initialData?.isActive ?? true,
    isFeatured: initialData?.isFeatured ?? false,
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        logo: initialData.logo || '',
        description: initialData.description || '',
        website: initialData.website || '',
        industry: initialData.industry || '',
        contactName: initialData.contactName || '',
        contactEmail: initialData.contactEmail || '',
        contactPhone: initialData.contactPhone || '',
        address: initialData.address || '',
        city: initialData.city || '',
        country: initialData.country || '',
        isActive: initialData.isActive ?? true,
        isFeatured: initialData.isFeatured ?? false,
      });
    }
  }, [initialData]);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateClientDto, string>>
  >({});

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateClientDto, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Client name is required';
    }

    if (
      formData.contactEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)
    ) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Website must start with http:// or https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error for this field
    if (errors[name as keyof CreateClientDto]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleLogoChange = (url: string | null) => {
    setFormData((prev) => ({ ...prev, logo: url || '' }));
    if (errors.logo) {
      setErrors((prev) => ({ ...prev, logo: undefined }));
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Essential Information - Always Visible */}
        <div className="space-y-6">
          {/* Client Name */}
          <div className="space-y-2">
            <Label htmlFor="name" required className="text-base font-medium">
              Client Name
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter client name"
              error={!!errors.name}
              className="text-lg"
              autoComplete="organization"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-base font-medium">
              Description
            </Label>
            <textarea
              id="description"
              name="description"
              rows={4}
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief description of the client and their business"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-base"
            />
          </div>

          {/* Logo Upload */}
          <div className="space-y-2">
            <ImageUploader
              value={formData.logo || null}
              onChange={handleLogoChange}
              onUploadStart={() => setUploading(true)}
              onUploadComplete={() => setUploading(false)}
              onUploadError={(error) => {
                setUploading(false);
                setErrors((prev) => ({ ...prev, logo: error }));
              }}
              folder="clients"
              maxSize={5}
              disabled={isSubmitting || uploading}
              label="Logo"
              helpText="PNG, JPG, SVG up to 5MB"
            />
            {errors.logo && (
              <p className="text-sm text-red-600">{errors.logo}</p>
            )}
          </div>
        </div>

        {/* Advanced Options Toggle */}
        <div className="border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-between w-full text-left group"
          >
            <span className="text-base font-medium text-gray-900 group-hover:text-indigo-600">
              Additional Details
            </span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${
                showAdvanced ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {/* Advanced Fields - Collapsible */}
        {showAdvanced && (
          <div className="space-y-6 animate-fadeIn">
            {/* Contact Information */}
            <div className="bg-gray-50 rounded-lg p-6 space-y-6">
              <h3 className="text-lg font-medium text-gray-900">
                Contact Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contactName">Contact Name</Label>
                  <Input
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    placeholder="John Doe"
                    autoComplete="name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Contact Email</Label>
                  <Input
                    id="contactEmail"
                    name="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    error={!!errors.contactEmail}
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPhone">Contact Phone</Label>
                  <Input
                    id="contactPhone"
                    name="contactPhone"
                    type="tel"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    autoComplete="tel"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://example.com"
                    error={!!errors.website}
                    autoComplete="url"
                  />
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="bg-gray-50 rounded-lg p-6 space-y-6">
              <h3 className="text-lg font-medium text-gray-900">
                Business Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    placeholder="e.g., Technology, Healthcare"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="United States"
                    autoComplete="country"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="New York"
                    autoComplete="address-level2"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Main Street"
                    autoComplete="street-address"
                  />
                </div>
              </div>
            </div>

            {/* Status Settings */}
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Settings</h3>

              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <div className="ml-3">
                    <Label htmlFor="isActive" className="font-medium">
                      Active
                    </Label>
                    <p className="text-sm text-gray-500">
                      Client is currently active and visible
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isFeatured"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <div className="ml-3">
                    <Label htmlFor="isFeatured" className="font-medium">
                      Featured
                    </Label>
                    <p className="text-sm text-gray-500">
                      Show this client prominently
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 space-y-reverse sm:space-y-0 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting || uploading}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || uploading}
            loading={isSubmitting}
            className="w-full sm:w-auto"
          >
            {uploading
              ? 'Uploading...'
              : initialData
                ? 'Update Client'
                : 'Create Client'}
          </Button>
        </div>
      </form>
    </div>
  );
};
